/**
 * iter87.47 — useRivalriesIntel hook.
 * iter87.49 — Tournament-scoped (was league-scoped).
 * iter87.50 — Surface pre-odds state so the chip stays visible for
 *             eligible tournaments even before spread lines land.
 *
 * Fetches the current-week intel document for a TOURNAMENT. Silently
 * handles 402 (Free tier — feature is Premium-only) and 404 (feature
 * flag off OR tournament doesn't exist). In both silent-failure cases
 * the hook returns `{ intel: null, visible: false }`, which the
 * TodoChips layer reads to mean "hide the chip".
 *
 * The tile only surfaces when the tournament uses spread-mode scoring
 * OR pot pickem so ranking-only tournaments never trigger the call.
 */
import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import { isFootballPlayoffsTemplate } from "@/lib/footballPlayoffsTemplates";

export function useRivalriesIntel(tournament) {
    const tournamentId = tournament?.id;
    // iter87.465 (user-requested) — NFL/CFB Playoffs moved to the
    // "show every game" Playoff Series Intel surface (same as NWSL),
    // so they no longer surface this weekly Top-3-EV tile at all.
    const eligible = (
        !isFootballPlayoffsTemplate(tournament?.template)
        && (tournament?.matchday_boost?.scoring_mode === "spread"
            || tournament?.pickem?.mode === "pot")
    );
    const [intel, setIntel] = useState(null);
    const [ready, setReady] = useState(false);

    const load = useCallback(async () => {
        if (!eligible || !tournamentId) {
            setIntel(null);
            setReady(true);
            return;
        }
        try {
            const data = await api.getRivalriesIntel(tournamentId);
            setIntel(data);
        } catch (e) {
            // 402/404/5xx: silently hide.
            setIntel(null);
        } finally {
            setReady(true);
        }
    }, [tournamentId, eligible]);

    useEffect(() => { load(); }, [load]);

    // iter87.50 — Backend now returns an `eligibility_state` field
    // (`"has_data"` when top5 populated, `"awaiting_odds"` when the
    // tournament is eligible but spreads haven't landed yet). The tile
    // stays visible in both cases; only 402/404/network errors hide it.
    // iter87.269 (user-reported) — "no_fixtures" (next real fixture is
    // simply further out than the 14-day window) must ALSO keep the
    // tile visible for entitled tournaments — a Pro+ user should never
    // see the tile just vanish. Only "ineligible" (wrong template/
    // lifecycle) and the null-on-error cases hide it.
    const hasData = !!intel && Array.isArray(intel.top5) && intel.top5.length > 0;
    const awaitingOdds = !!intel && !hasData
        && intel.eligibility_state === "awaiting_odds";
    const outsideWindow = !!intel && !hasData
        && intel.eligibility_state === "no_fixtures";
    const visible = hasData || awaitingOdds || outsideWindow;
    return { intel, hasData, awaitingOdds, outsideWindow, visible, ready, refresh: load };
}
